import { Widget } from "blue-widgets";

class Expandable extends Widget {
  constructor(el) {
    super(el);
    this.transitionDuration = null;
    this.setTransitionDuration = this.setTransitionDuration.bind(this);

    this.init();
  }

  init() {
    this.trigger = this.el.querySelector("[data-accordion-trigger]");
    if (!this.trigger) {
      return;
    }
    const contentId = this.trigger.getAttribute("aria-controls");

    this.content = this.el.querySelector(`#${contentId}`);
    this.maxHeight = this.el.getAttribute("data-max") || 800;
    this.toggleContent = this.toggleContent.bind(this);
    this.handleKeypress = this.handleKeypress.bind(this);

    this.trigger.addEventListener("click", this.toggleContent);
    this.trigger.addEventListener("keydown", this.handleKeypress);

    this.el.addEventListener("close-expandable", this.hideContent.bind(this));

    // Hide child components from keyboard focus initially
    if (this.content) {
      this.toggleContentTabindexs("-1");
      this.content.setAttribute("aria-hidden", "true");
    }

    // Set transition duration
    this.setTransitionDuration();
    if (this.options.isopen) {
      this.toggleContent();
    }
  }

  toggleContent() {
    const isExpanded =
      this.trigger.getAttribute("aria-expanded") === "true" ||
      this.trigger
        .querySelector("[aria-expanded]")
        ?.getAttribute("aria-expanded") === "true";
    if (isExpanded) {
      this.el.classList.remove("isOpen");
      if (this.trigger.getAttribute("aria-expanded")) {
        this.trigger.setAttribute("aria-expanded", "false");
      } else {
        this.trigger
          .querySelector("[aria-expanded]")
          .setAttribute("aria-expanded", "false");
      }

      if (this.content) {
        this.toggleContentTabindexs("-1");
        this.content.setAttribute("aria-hidden", "true");
      }
    } else {
      this.el.classList.add("isOpen");

      if (this.trigger.getAttribute("aria-expanded")) {
        this.trigger.setAttribute("aria-expanded", "true");
      } else {
        this.trigger
          .querySelector("[aria-expanded]")
          .setAttribute("aria-expanded", "true");
      }

      if (this.content) {
        this.toggleContentTabindexs("0");
        this.content.setAttribute("aria-hidden", "false");
      }
    }
    this.dispatchCustomEvent();
    // Hide scrollbar while transitioning
    if (this.transitionDuration) {
      this.content.style.overflowY = "hidden";
      setTimeout(() => {
        this.content.style.overflowY = "";
      }, this.transitionDuration);
    }
  }

  toggleContentTabindexs(tabIndex) {
    const tabbableElements = this.content.querySelectorAll(
      "a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])"
    );

    const tabbableElementsArray = Array.from(tabbableElements);
    // Make the content focussable so they can tab into it even if there is only text
    this.content.setAttribute("tabindex", tabIndex);
    tabbableElementsArray.forEach((element) => {
      element.setAttribute("tabindex", tabIndex);
    });
  }

  setTransitionDuration() {
    const computedStyle = window.getComputedStyle(this.content);

    this.transitionDuration =
      parseFloat(computedStyle.getPropertyValue("transition-duration")) * 1000;
  }

  hideContent() {
    this.el.classList.remove("isOpen");

    if (this.trigger.hasAttribute("aria-expanded")) {
      this.trigger.setAttribute("aria-expanded", "false");
    } else {
      this.trigger
        .querySelector("[aria-expanded]")
        .setAttribute("aria-expanded", "false");
    }

    if (this.content) {
      this.toggleContentTabindexs("-1");
      this.content.setAttribute("aria-hidden", "true");
    }
  }

  dispatchCustomEvent() {
    const customEvent = new CustomEvent("expandable-toggled", {
      detail: {
        state: !this.isExpanded,
      },
    });
    this.el.dispatchEvent(customEvent);
  }

  handleKeypress(e) {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      this.toggleContent();
    }
  }

  beforeRemove() {
    this.trigger.removeEventListener("click", this.toggleContent);
    this.trigger.removeEventListener("keydown", this.handleKeypress);
  }
}

export default Expandable;
